import { ContainerComponent, wrap } from 'AppReact';
import Controller from './controller';
import {
  RoundedFilterHeaderWrapper, WhiteSpaceNoWrap, Mr3, Mb2, Mt2,
  GenericWrapper, FitRelativeWrapper, AbsoluteLeftTopWrapper, TooltipWrapper,
  CustomBorderWrapper, FlexCenterBetweenWrapper, Px3, Mt4, P3, Mr2,
  CustomHeight, FullWIdthColumnScrollWrapper, PrimaryBackground50Wrapper,
  FullWidthFlexBoxWrapper, MaxWidthHeight, FlexCenterWrapper, FullWidthJustifyEndWrapper
} from 'wrappers';
import internalState from './internalState';
import { SecondaryTextSm, BorderlessInput, Icon, SecondaryTextXs, Tooltip } from 'atoms';
import { MagnifyingGlassSvg, DownCaretSvg } from 'components/icons';
import { TextButton, PropertyButton, SelectedProperty } from 'molecules';
import { Property } from './molecules';
import { textEllipsis } from 'utils/ellipsis';
import { escapedRegex } from 'utils';

export const definition = {
  name: 'MultiPropertySelector',
  controller: Controller,
  initialProps: ({ properties, selectedPropertyIds, label, isReadonly, headerWidth, searchPlaceholder, maxCount }) =>
    ({ properties, selectedPropertyIds, label, isReadonly, headerWidth, searchPlaceholder, maxCount }),
  props: internalState,
  elements: [
    {
      wrapper: FitRelativeWrapper,
      name: 'Wrapper',
      ref: ({ containerRef }) => containerRef,
      elements: [
        {
          wrapper: RoundedFilterHeaderWrapper,
          name: 'Header',
          isButton: true,
          displayState: ({ isReadonly, selectedPropertyIds, isOpen, headerWidth }) => ({ disabled: isReadonly, completed: selectedPropertyIds.length > 0 && !isOpen, width: headerWidth }),
          bindings: ({ isOpen, isReadonly, selectedPropertyIds, containerRef, inputRef }) => ({ isOpen, containerRef, isReadonly, inputRef, selectedPropertyIds }),
          elements: [
            {
              component: wrap(wrap(SecondaryTextSm, WhiteSpaceNoWrap), Mr3),
              renderIf: ({ isOpen }) => !isOpen,
              displayValue: ({ label, selectedPropertyIds }) => {
                if (label) return label;
                if (selectedPropertyIds.length > 0) {
                  return 'Properties';
                }
                return 'Filter by property';
              }
            },
            {
              wrapper: GenericWrapper,
              name: 'SearchWrapper',
              ref: ({ inputRef }) => inputRef ?? null,
              elements: [
                {
                  component: wrap(BorderlessInput, Mr3),
                  name: 'SearchInput',
                  renderIf: ({ isOpen }) => isOpen,
                  displayState: ({ searchPlaceholder }) => ({ width: searchPlaceholder ? '150px' : '114px', minWidth: '114px' }),
                  bindings: ({ searchTerm, searchPlaceholder }) => ({ value: searchTerm, placeholder: searchPlaceholder ?? "Search..." })
                },
              ]
            },
            {
              component: wrap(SelectedProperty, Mr3),
              renderIf: ({ selectedPropertyIds, isOpen }) => selectedPropertyIds.length > 0 && !isOpen,
              name: 'SelectedProperties',
              bindings: ({ selectedPropertyIds }) => ({ count: selectedPropertyIds.length })
            },
            {
              component: wrap(Icon, FullWidthJustifyEndWrapper),
              name: 'Icon',
              displayValue: ({ isOpen }) => isOpen ? MagnifyingGlassSvg({ color: '#C2CCCE' }) : DownCaretSvg({ color: '#C2CCCE' })
            }
          ]
        },
        {
          wrapper: AbsoluteLeftTopWrapper,
          displayState: ({ }) => ({ top: '50px' }),
          renderIf: ({ isOpen }) => isOpen,
          name: 'DropdownWrapper',
          elements: [
            {
              wrapper: CustomBorderWrapper,
              name: 'BorderWrapper',
              displayState: ({ }) => ({ borderRadius: '8px', border: '1px solid #E3E3E3', background: '#FFFFFF' }),
              elements: [
                {
                  wrapper: wrap(P3, PrimaryBackground50Wrapper),
                  renderIf: ({ selectedPropertyIds }) => selectedPropertyIds.length > 0,
                  name: 'ButtonsWrapper',
                  elements: [
                    {
                      wrapper: MaxWidthHeight,
                      name: 'ListWrapper',
                      displayState: ({ }) => ({ height: '52px', width: '100%', display: 'flex' }),
                      elements: [
                        {
                          wrapper: wrap(wrap(FullWidthFlexBoxWrapper, FullWIdthColumnScrollWrapper), FlexCenterWrapper),
                          name: 'ButtonsWrapper',
                          elements: [
                            {
                              component: wrap(wrap(PropertyButton, Mr2), Mt2),
                              name: 'PropertyButton',
                              isListOf: 'selectedPropertyIds',
                              bindings: ({ properties, selectedPropertyId, selectedPropertyIds }) => ({ selectedPropertyId, selectedPropertyIds, icon: properties.find(p => p.id === selectedPropertyId)?.icon, name: properties.find(p => p.id === selectedPropertyId)?.name })
                            },
                          ]
                        },
                      ]
                    },
                  ]
                },
                {
                  wrapper: wrap(wrap(wrap(FlexCenterBetweenWrapper, Px3), Mt4), Mb2),
                  name: 'ClearAllHeader',
                  elements: [
                    {
                      component: SecondaryTextXs,
                      name: 'SelectedText',
                      displayValue: ({ selectedPropertyIds }) => `${(selectedPropertyIds.length ?? 0)} Selected`
                    },
                  ]
                },
                {
                  wrapper: CustomHeight,
                  name: 'ListWrapper',
                  displayState: ({ headerWidth }) => ({ height: 270, width: headerWidth ? headerWidth : '300px' }),
                  elements: [
                    {
                      wrapper: FullWIdthColumnScrollWrapper,
                      name: 'ListWrapper',
                      elements: [
                        {
                          wrapper: TooltipWrapper,
                          name: '__transparent__',
                          renderIf: ({ property, searchTerm }) => escapedRegex(searchTerm).test(property.name),
                          isListOf: 'properties',
                          elements: [
                            {
                              component: wrap(Property, Mt2),
                              name: 'Property',
                              bindings: ({ property, selectedPropertyIds, headerWidth, maxCount }) => {
                                const isSelected = selectedPropertyIds.includes(property.id);
                                return {
                                  icon: property?.icon ?? "",
                                  propertyName: headerWidth ? textEllipsis(property.name, 20) : property.name,
                                  id: property.id,
                                  isSelected,
                                  selectedPropertyIds,
                                  disabled: selectedPropertyIds?.length > maxCount - 1 && !isSelected
                                }
                              },
                              displayState: ({ selectedPropertyIds, property, maxCount }) => {
                                const isSelected = selectedPropertyIds.includes(property.id);
                                return {
                                  isSelected,
                                  disabled: selectedPropertyIds?.length > maxCount - 1 && !isSelected
                                }
                              }
                            },
                            {
                              component: Tooltip,
                              name: 'BellTooltip',
                              renderIf: ({ selectedPropertyIds, property, maxCount }) => {
                                const isSelected = selectedPropertyIds.includes(property.id);
                                return selectedPropertyIds?.length > maxCount - 1 && !isSelected
                              },
                              displayValue: ({ maxCount }) => `Can only compare ${maxCount} properties at a time`,
                              displayState: ({ }) => ({ direction: 'bottom', width: '225px' })
                            }
                          ]
                        }
                      ]
                    },
                  ]
                }
              ]
            }
          ]
        }

      ]
    }
  ]
};

export default ContainerComponent(definition)